import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import projectService from '../services/projectService';
import meetingService from '../services/meetingService';
import ProjectDialog from '../components/common/ProjectDialog';
import UniversalCard from '../components/common/UniversalCard';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ProjectDetailsPage = () => {
  const { projectId } = useParams();
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();

  const isAdmin = currentUser && (currentUser.profile?.isAdmin || currentUser.isAdmin);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [project, setProject] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [projectSummary, setProjectSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = projectService.onProjectById(projectId, async (updatedProject) => {
      if (!updatedProject) {
        addSystemNotification('Projet non trouvé', 'error');
        navigate('/projects');
        return;
      }

      setProject(updatedProject);
      setLoading(false);

      if (updatedProject.meetingIds && updatedProject.meetingIds.length > 0) {
        loadMeetings(updatedProject.meetingIds);
      } else {
        setMeetings([]);
        setLoadingMeetings(false);
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [projectId, navigate]);

  useEffect(() => {
    if (!projectId) return;

    setLoadingSummary(true);

    const unsubscribe = projectService.onProjectSummary(projectId, (summary) => {
      setProjectSummary(summary);
      setLoadingSummary(false);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [projectId]);

  const loadMeetings = async (meetingIds) => {
    setLoadingMeetings(true);
    try {
      const meetingsData = [];
      for (const meetingId of meetingIds) {
        try {
          const meeting = await meetingService.getMeetingById(meetingId);
          if (meeting) {
            meetingsData.push(meeting);
          }
        } catch (error) {
        }
      }
      meetingsData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setMeetings(meetingsData);
    } catch (error) {
      addSystemNotification('Erreur lors du chargement des réunions', 'error');
    } finally {
      setLoadingMeetings(false);
    }
  };

  const handleBack = () => {
    navigate('/projects');
  };

  const handleDeleteProject = async () => {
    if (!project) return;

    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer le projet "${project.name}" ? Les réunions ne seront pas supprimées.`
      )
    ) {
      try {
        await projectService.deleteProject(project.id);
        addSystemNotification('Projet supprimé avec succès', 'success');
        navigate('/projects');
      } catch (error) {
        addSystemNotification('Erreur lors de la suppression du projet', 'error');
      }
    }
  };

  const handleMeetingClick = (meetingId) => {
    navigate(`/meetings/${meetingId}`);
  };

  const handleRemoveMeeting = async (meetingId) => {
    if (!project) return;

    try {
      await projectService.removeMeetingFromProject(project.id, meetingId);
      addSystemNotification('Réunion retirée du projet', 'success');
    } catch (error) {
      addSystemNotification('Erreur lors du retrait de la réunion', 'error');
    }
  };

  const handleGenerateSummary = async () => {
    if (!project) return;

    setGeneratingSummary(true);

    try {
      await projectService.generateProjectSummary(project.id);
      addSystemNotification('Résumé du projet généré avec succès', 'success');
    } catch (error) {
      addSystemNotification(error.message || 'Erreur lors de la génération du résumé', 'error');
    } finally {
      setGeneratingSummary(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" textAlign="center" px={3}>
        <FolderIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Accès restreint
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          Cette fonctionnalité est réservée aux administrateurs.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/meetings')} sx={{ mt: 3 }}>
          Retour aux réunions
        </Button>
      </Box>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: isMobile ? 10 : 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: project.color || '#1976d2',
            flexShrink: 0
          }}
        />
        <Typography variant="h4" fontWeight="bold" flex={1} noWrap>
          {project.name}
        </Typography>
        <IconButton onClick={() => setDialogOpen(true)} color="primary">
          <EditIcon />
        </IconButton>
        <IconButton onClick={handleDeleteProject} color="error">
          <DeleteIcon />
        </IconButton>
      </Stack>

      {project.description && (
        <Alert severity="info" icon={<FolderIcon />} sx={{ mb: 3 }}>
          {project.description}
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={4}>
        <Chip
          icon={<FolderIcon />}
          label={`${project.meetingIds?.length || 0} réunion${
            (project.meetingIds?.length || 0) > 1 ? 's' : ''
          }`}
          color="primary"
        />
        {project.createdAt && (
          <Chip
            label={`Créé le ${format(new Date(project.createdAt), 'dd MMMM yyyy', { locale: fr })}`}
            variant="outlined"
          />
        )}
      </Stack>

      {project.meetingIds && project.meetingIds.length > 0 && (
        <Paper elevation={0} sx={{ mb: 4, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Résumé du projet
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={generatingSummary ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleGenerateSummary}
              disabled={generatingSummary}
            >
              {projectSummary ? 'Régénérer' : 'Générer'}
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {loadingSummary ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : projectSummary ? (
            <Box>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  color: 'text.secondary',
                  lineHeight: 1.7
                }}
              >
                {projectSummary.text}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                Dernière mise à jour: {format(new Date(projectSummary.updatedAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </Typography>
            </Box>
          ) : (
            <Alert severity="info" sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                Aucun résumé généré pour ce projet. Cliquez sur "Générer" pour créer un résumé consolidé de toutes les réunions.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}

      <Typography variant="h5" fontWeight="bold" mb={2}>
        Réunions du projet
      </Typography>

      {loadingMeetings ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : meetings.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Aucune réunion dans ce projet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Créez une nouvelle réunion et associez-la à ce projet
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/meetings')}
          >
            Aller aux réunions
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {meetings.map((meeting) => (
            <Grid item xs={12} key={meeting.id}>
              <UniversalCard
                meeting={meeting}
                onClick={() => handleMeetingClick(meeting.id)}
                onRemoveFromProject={() => handleRemoveMeeting(meeting.id)}
                showProjectBadge={false}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => setDialogOpen(false)}
        projectId={project.id}
      />
    </Box>
  );
};

export default ProjectDetailsPage;
